import React, { useCallback, useEffect, useRef, useState } from "react";
import Drawflow from "drawflow";
import "drawflow/dist/drawflow.min.css";
import "./drawflow.css";
import ReactDOM from "react-dom/client";
import CampaignNodeContent from "./nodes/campaign/CampaignNodeContent";
import Sidebar from "./sidebar/Sidebar";
import { fetchCampaigns, fetchIdentities, fetchUserTemplates, getCustomerGroupsRes, createCampaignsRes } from "core";
import { getUploadUrls, uploadToS3, confirmSave } from "../../../core/features/content-builder/api";
import { Button } from "components/ui/button";
import { ZoomIn, ZoomOut, Zap } from "lucide-react"
import { toast } from "components/ui/toast";
import { useNavigate } from "react-router-dom";

type NodeType = "template" | "lead" | "campaign" | "identity";
interface BaseNodeData {
    id?: number;
    type: NodeType;
    name: string;
    subject?: string;
    startDate?: string;
    endDate?: string;
    isEditable?: boolean;
    hideSubject?: boolean;
    parentId?: number;
    childIds?: number[];
    templateNodeId?: number | null;
    leadNodeId?: number | null;
    emailNodeName?: string | null;

    inputs?: number;
    outputs?: number;
}

interface TemplateItem { id: number; name: string;[k: string]: any }
interface LeadItem { id: number; name: string;[k: string]: any }
interface CampaignItem { id: number; name: string;[k: string]: any }
interface IdentityItem { email: string; friendlyName?: string; name?: string;[k: string]: any }


class DrawflowOverride extends (Drawflow as any) {
    removeNodeById(id: number) {
        const nodeId = `node-${id}`;
        // @ts-ignore private method on instance
        this.removeNodeId(nodeId);
    }
}

const initialNodesData: Array<{ name: string; content: string; x: number; y: number; label: string; icon: string }> = [
    { name: "Template", content: "Template content", x: 50, y: 50, label: "<div><b>Template</b></div>", icon: "bi bi-columns" },
    { name: "Leads", content: "Leads content", x: 50, y: 150, label: "<div><b>Leads</b></div>", icon: "bi bi-award-fill" },
    { name: "Campaign", content: "Campaign", x: 50, y: 250, label: "<div><b>Campaign</b></div>", icon: "bi bi-activity" },
    { name: "Select Identity", content: "Select Identity", x: 50, y: 350, label: "<div><b>Execute</b></div>", icon: "bi bi-hand-index-thumb" },
];

const DrawflowComponent: React.FC = () => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const editorRef = useRef<any>(null);
    const navigate = useNavigate();
    const [nodeCount, setNodeCount] = useState(0);


    const [nodesData] = useState(initialNodesData);
    const [templates, setTemplates] = useState<TemplateItem[]>([]);
    const [leads, setLeads] = useState<LeadItem[]>([]);
    const [identities, setIdentities] = useState<IdentityItem[]>([]);
    const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);
    const [editableNode, setEditableNode] = useState<BaseNodeData | null>(null);
    const [_zoom, setZoom] = useState<number>(1);

    const [isCanvasLoading, setIsCanvasLoading] = useState(false);
    const [sidebarLoading, setSidebarLoading] = useState<string | null>(null);
    const [templateNameModal, setTemplateNameModal] = useState<{ open: boolean; value: string; tag: string }>({ open: false, value: '', tag: '' });

    const fetchTemplates = useCallback(async () => {
        setSidebarLoading("templates");
        try {
            const res = await fetchUserTemplates({ paginator: 0 });
            setTemplates(res.items || []);
        } catch (e) {
            console.error("Failed to fetch templates:", e);
        } finally {
            setSidebarLoading(null);
        }
    }, []);

    const fetchCampaignsData = useCallback(async () => {
        setSidebarLoading("campaigns");
        try {
            const res = await fetchCampaigns({
                page: 1,
                active: 1,
                progress: 0,
                pc: 0,
                comp: 1,
                terminated: 0
            });
            setCampaigns(res.items || []);
        } catch (e) {
            console.error("Failed to fetch campaigns:", e);
        } finally {
            setSidebarLoading(null);
        }
    }, []);

    const fetchLeads = useCallback(async () => {
        setSidebarLoading("leads");
        try {
            const res = await getCustomerGroupsRes();
            setLeads(res || []);
        } catch (e) {
            console.error("Failed to fetch leads:", e);
        } finally {
            setSidebarLoading(null);
        }
    }, []);

    const fetchIdentitiesData = useCallback(async () => {
        setSidebarLoading("identities");
        try {
            const res = await fetchIdentities();
            setIdentities(res.items || []);
        } catch (e) {
            console.error("Failed to fetch identities:", e);
            setIdentities([]);
        } finally {
            setSidebarLoading(null);
        }
    }, []);

    const fetchAll = useCallback(async () => {
        setIsCanvasLoading(true);
        try {
            await Promise.all([fetchTemplates(), fetchCampaignsData(), fetchLeads(), fetchIdentitiesData()]);
        } catch (e) {
            console.error("Failed to fetch all data:", e);
        } finally {
            setIsCanvasLoading(false);
        }
    }, [fetchTemplates, fetchCampaignsData, fetchLeads, fetchIdentitiesData]);

    const initDrawflow = useCallback(() => {
        const container: any = containerRef.current;
        if (!container) return;

        const editor: any = new (DrawflowOverride as any)(container);
        editorRef.current = editor;

        editor.start();
        editor.reroute = true;
        editor.editor_mode = "edit";

        const updateNodeCount = () => {
            const data = editor.export()?.drawflow?.Home?.data || {};
            setNodeCount(Object.keys(data).length);
        };

        editor.on("nodeCreated", () => updateNodeCount());
        editor.on("nodeRemoved", () => updateNodeCount());

        container.addEventListener("drop", (e) => dropNode(e as DragEvent));
        container.addEventListener("dragover", (e) => e.preventDefault());
        editor.on("connectionCreated", (conn: any) => validateConnection(conn));
        editor.on("connectionRemoved", (conn: any) => handleConnectionRemoved(conn));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const removeNode = useCallback((nodeId: number) => {
        const editor = editorRef.current;
        if (!editor) return;
        const node = editor.getNodeFromId(nodeId);
        if (node) {
            editor.removeNodeId(`node-${nodeId}`);
            setNodeCount((c) => Math.max(0, c - 1));
        }
        else console.error(`Node with ID ${nodeId} not found or already removed.`);
    }, []);

    const addNodeToDrawflow = useCallback((nodeData: BaseNodeData) => {
        const container = containerRef.current;
        const editor = editorRef.current;
        if (!container || !editor) return;

        const rect = container.getBoundingClientRect();
        const x = rect.width / 2 - 100;
        const y = rect.height / 2 - 50;

        addNodeToDrawflowInternal(nodeData, x, y);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addNodeToDrawflowInternal = useCallback((nodeData: any, x: number, y: number) => {
        const editor = editorRef.current;
        if (!editor) return;

        const createNodeTemplate = (content = "", nodeType = "", nodeId: number, inputs = 0, outputs = 0) => {
            return `
        <div class="node-content parent-node type-${nodeType.toLowerCase()} relative" id="node-${nodeId}">
          <div class="node-header">${nodeType}</div>
          <div class="cancel-button">×</div>
          <div>${content}</div>
          ${nodeType.toLowerCase() !== "identity"
                    ? `<div class="arrow arrow-right absolute" data-node-id="${nodeId}" data-node-type="${nodeType}" data-inputs="${inputs}" data-outputs="${outputs}">
                  <span class="tooltip"></span>
                </div>`
                    : ""
                }
          ${nodeType.toLowerCase() === "campaign" || nodeType.toLowerCase() === "identity"
                    ? `<div class="arrow arrow-right-left absolute" data-node-id="${nodeId}" data-node-type="${nodeType}" data-inputs="${inputs}" data-outputs="${outputs}">
                  <span class="tooltip"></span>
                </div>`
                    : ""
                }
        </div>`;
        };

        let nodeTemplate = "";
        const nodeTypeHeading = nodeData.type.toUpperCase();

        if (nodeData.type === "campaign") {
            nodeData.inputs = 1 as any;
            nodeData.outputs = 1 as any;
            nodeTemplate = createNodeTemplate(`<div id="react-node-${/* placeholder id */ ""}" data-react-slot="true"></div>`, nodeTypeHeading, 0, 1, 1);
        } else if (nodeData.type === "template" || nodeData.type === "lead") {
            nodeData.inputs = 0 as any;
            nodeData.outputs = 1 as any;
            nodeTemplate = createNodeTemplate(`<div class="node-single-value">${nodeData.name}</div>`, nodeTypeHeading, 0, 0, 1);
        } else if (nodeData.type === "identity") {
            nodeData.inputs = 1 as any;
            nodeData.outputs = 0 as any;
            nodeTemplate = createNodeTemplate(`<div class="node-single-value">${nodeData.name}</div>`, nodeTypeHeading, 0, 1, 0);
        }


        const nodeId = editor.addNode(
            nodeData.name,
            (nodeData as any).inputs ?? 0,
            (nodeData as any).outputs ?? 0,
            x,
            y,
            nodeData.name,
            nodeData,

            nodeTemplate.replace(/id="node-0"/, `id="node-${editor.nodeId}"`).replace(/react-node-"/, `react-node-${editor.nodeId}"`)
        );



        setTimeout(() => {
            mountNodeContent(nodeId, nodeData);
            setNodeCount((c) => c + 1);
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEditNode = useCallback((nodeData: any) => {
        console.log("Edit node clicked:", nodeData);
        setEditableNode(nodeData);
    }, []);

    const handleUpdateCampaignNodeData = useCallback((updatedData: any) => {
        const editor = editorRef.current;
        if (!editor || !updatedData.id) return;

        const node = editor.getNodeFromId(updatedData.id);
        if (!node) {
            console.error(`Node with ID ${updatedData.id} not found`);
            return;
        }


        const newData = {
            ...node.data,
            name: updatedData.name,
            subject: updatedData.subject,
            startDate: updatedData.startDate,
            endDate: updatedData.endDate,
        };

        editor.updateNodeDataFromId(updatedData.id, newData);


        const nodeEl = document.getElementById(`node-${updatedData.id}`);
        if (nodeEl) {
            const slot = nodeEl.querySelector(`[data-react-slot="true"]`) as HTMLDivElement | null;
            if (slot) {
                const root = ReactDOM.createRoot(slot);
                root.render(
                    <CampaignNodeContent
                        name={updatedData.name}
                        subject={updatedData.subject}
                        startDate={updatedData.startDate}
                        endDate={updatedData.endDate}
                        nodeId={updatedData.id}
                        isEditable={newData.isEditable !== false}
                        hideSubject={newData.hideSubject || false}
                        onEditNode={handleEditNode}
                    />
                );
            }
        }

        setEditableNode(null);
    }, [handleEditNode]);

    const mountNodeContent = useCallback((nodeId: number, nodeData: any) => {
        const editor = editorRef.current;
        if (!editor) return;

        const nodeEl = document.getElementById(`node-${nodeId}`);
        if (!nodeEl) return;

        const cancelBtn = nodeEl.querySelector(".cancel-button") as HTMLDivElement | null;
        if (cancelBtn) {
            cancelBtn.addEventListener("click", () => {
                removeNode(nodeId);
            });
        }


        const arrowRight = nodeEl.querySelector(".arrow-right") as HTMLDivElement | null;
        const arrowRL = nodeEl.querySelector(".arrow-right-left") as HTMLDivElement | null;

        const editorExport = () => (editor.export() as any)?.drawflow?.Home?.data || {};
        const getNode = (id: number) => editor.getNodeFromId(id);

        if (arrowRight) {
            const tip = arrowRight.querySelector(".tooltip") as HTMLSpanElement | null;
            arrowRight.addEventListener("mouseover", () => {
                const nodeType = arrowRight.getAttribute("data-node-type")?.toLowerCase();
                if (!tip || !nodeType) return;

                let suggestion = "";
                if (nodeType === "campaign") {
                    const allNodes = Object.values(editorExport());
                    const campaignNode: any = allNodes.find((n: any) => n?.data?.type?.toLowerCase() === "campaign");
                    if (!campaignNode) {
                        arrowRight.style.display = "none";
                        return;
                    }
                    const connections = campaignNode?.outputs?.output_1?.connections || [];
                    const isConnectedToIdentity = connections.some((c: any) => {
                        const n = getNode(c.node);
                        return n?.data?.type === "identity";
                    });
                    if (isConnectedToIdentity) {
                        arrowRight.style.display = "none";
                        tip.style.display = "none";
                        return;
                    }
                    suggestion = "Identity Output";
                } else if (nodeType === "lead" || nodeType === "template") {
                    suggestion = "Campaigns Output";
                }
                tip.textContent = suggestion;
                tip.style.display = suggestion ? "block" : "none";
            });
        }

        if (arrowRL) {
            const tip = arrowRL.querySelector(".tooltip") as HTMLSpanElement | null;
            arrowRL.addEventListener("mouseover", () => {
                const nodeType = arrowRL.getAttribute("data-node-type")?.toLowerCase();
                if (!tip || !nodeType) return;

                let suggestion = "";
                const allNodes = Object.values(editorExport());

                if (nodeType === "campaign") {
                    const campaignNode: any = allNodes.find((n: any) => n?.data?.type?.toLowerCase() === "campaign");
                    if (!campaignNode) {
                        arrowRL.style.display = "none";
                        return;
                    }
                    const connected = campaignNode?.inputs?.input_1?.connections || [];
                    const types = connected.map((c: any) => getNode(c.node)?.data?.type);
                    const hasTemplate = types.includes("template");
                    const hasLead = types.includes("lead");

                    if (hasTemplate && hasLead) {
                        arrowRL.style.display = "none";
                        return;
                    }
                    if (!hasTemplate && !hasLead) suggestion = "Template & Lead Input";
                    else if (!hasTemplate) suggestion = "Template Input";
                    else if (!hasLead) suggestion = "Lead Input";
                } else if (nodeType === "identity") {
                    const identityNode: any = allNodes.find((n: any) => n?.data?.type?.toLowerCase() === "identity");
                    if (!identityNode) {
                        arrowRL.style.display = "none";
                        return;
                    }
                    const connected = identityNode?.inputs?.input_1?.connections || [];
                    const isConnectedToCampaign = connected.some((c: any) => getNode(c.node)?.data?.type === "campaign");
                    if (!isConnectedToCampaign) suggestion = "Campaign Input";
                }

                tip.textContent = suggestion;
                tip.style.display = suggestion ? "block" : "none";
            });
        }


        if (nodeData.type === "campaign") {
            const slot = nodeEl.querySelector(`[data-react-slot="true"]`) as HTMLDivElement | null;
            if (slot) {
                const root = ReactDOM.createRoot(slot);
                root.render(
                    <CampaignNodeContent
                        name={nodeData.name}
                        subject={nodeData.subject}
                        startDate={nodeData.startDate}
                        endDate={nodeData.endDate}
                        nodeId={nodeId}
                        isEditable={nodeData.isEditable !== false}
                        hideSubject={nodeData.hideSubject || false}
                        onEditNode={handleEditNode}
                    />
                );
            }
        }
    }, [removeNode, handleEditNode]);

    const dropNode = useCallback((event: DragEvent) => {
        event.preventDefault();
        const container = containerRef.current;
        const editor = editorRef.current;
        if (!container || !editor) return;

        const rect = container.getBoundingClientRect();
        const x = (event.clientX || 0) - rect.left;
        const y = (event.clientY || 0) - rect.top;

        const payload = event.dataTransfer?.getData("application/json");
        if (!payload) return;

        try {
            const nodeData = JSON.parse(payload) as BaseNodeData;
            if (!nodeData?.type) throw new Error("Invalid node data");
            addNodeToDrawflowInternal(nodeData, x, y);
        } catch (err) {
            console.error("Error parsing dropped node data:", err);
        }
    }, [addNodeToDrawflowInternal]);


    const showInvalidConnection = (msg: string, connection: any) => {
        toast.error(msg);
        removeConnectionImmediately(connection);
    };

    const removeConnectionImmediately = (connection: any) => {
        const { output_id, input_id } = connection;
        const editor = editorRef.current;
        if (!editor) return;
        try {

            (editor as any).removeSingleConnection(output_id, input_id, "output_1", "input_1");
        } catch (e) {
            console.error("Error during connection removal:", (e as any)?.message);
        }
    };

    const validateConnection = (connection: any) => {
        const editor = editorRef.current;
        if (!editor) return;

        const { output_id, input_id } = connection;
        const outputNode = editor.getNodeFromId(output_id);
        const inputNode = editor.getNodeFromId(input_id);
        if (!outputNode || !inputNode) {
            showInvalidConnection("Invalid connection: Node(s) not found.", connection);
            return;
        }

        const invalidPairs: Array<[NodeType, NodeType]> = [
            ["template", "lead"],
            ["template", "identity"],
            ["lead", "identity"],
        ];

        const isInvalidPair = invalidPairs.some(
            ([a, b]) =>
                (outputNode.data.type === a && inputNode.data.type === b) ||
                (outputNode.data.type === b && inputNode.data.type === a)
        );

        if (isInvalidPair) {
            showInvalidConnection(
                `Invalid connection: ${outputNode.data.type} cannot be connected to ${inputNode.data.type}`,
                connection
            );
            return;
        }


        if (outputNode.data.type === "campaign" && inputNode.data.type === "identity") {
            const outConns = outputNode.outputs?.output_1?.connections || [];
            const identityCount = outConns.filter((c: any) => editor.getNodeFromId(c.node)?.data?.type === "identity").length;
            if (identityCount > 1) {
                showInvalidConnection(
                    "Invalid connection: Campaign node output cannot be connected to more than one Identity node.",
                    connection
                );
                return;
            }
        }


        if (inputNode.data.type === "campaign") {
            const inConns = inputNode.inputs?.input_1?.connections || [];
            const connectedNodes = inConns.map((c: any) => editor.getNodeFromId(c.node));
            const templateCount = connectedNodes.filter((n: any) => n?.data?.type === "template").length;
            const leadCount = connectedNodes.filter((n: any) => n?.data?.type === "lead").length;

            if ((outputNode.data.type === "template" && templateCount > 1) ||
                (outputNode.data.type === "lead" && leadCount > 1)) {
                showInvalidConnection(
                    `Invalid connection: ${inputNode.data.type} can only have one ${outputNode.data.type}`,
                    connection
                );
                return;
            }
        }


        if (outputNode.data.type === "campaign" && inputNode.data.type === "campaign") {
            setParentChildRelation(outputNode, inputNode);
            updateParentChildRelationship(inputNode);
        }
    };

    const setParentChildRelation = (parentNode: any, childNode: any) => {
        const editor = editorRef.current;
        if (!editor) return;

        childNode.data.parentId = parentNode.id;
        parentNode.data.childIds = parentNode.data.childIds || [];
        if (!parentNode.data.childIds.includes(childNode.id)) {
            parentNode.data.childIds.push(childNode.id);
        }


        if (!childNode.data.templateNodeId) childNode.data.templateNodeId = parentNode.data.templateNodeId;
        if (!childNode.data.leadNodeId) childNode.data.leadNodeId = parentNode.data.leadNodeId;
        if (!childNode.data.emailNodeName) childNode.data.emailNodeName = parentNode.data.emailNodeName;

        editor.updateNodeDataFromId(childNode.id, childNode.data);
        editor.updateNodeDataFromId(parentNode.id, parentNode.data);

    };

    const updateParentChildRelationship = (node: any) => {
        const editor = editorRef.current;
        if (!editor || !node || node.data.type !== "campaign") return;

        const children = node.outputs?.output_1?.connections || [];
        children.forEach((c: any) => {
            const childNode = editor.getNodeFromId(c.node);
            if (childNode?.data?.type === "campaign") {
                setParentChildRelation(node, childNode);
                updateParentChildRelationship(childNode);
            }
        });
    };

    const handleConnectionRemoved = (connection: any) => {
        console.log("Connection removed:", connection);
    };

    const clearNodes = () => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.clear();
        editor.import({ drawflow: { Home: { data: {} } } } as any);
        setNodeCount(0);
    };

    const getCampaignNodes = useCallback(() => {
        const editor = editorRef.current;
        if (!editor) return [];
        const data = (editor.export() as any)?.drawflow?.Home?.data || {};
        return Object.values<any>(data).filter((n) => n?.data?.type === "campaign");
    }, []);

    const getConnectedNodes = useCallback(
        (campaignNode: any) => {
            let templateNodeId = campaignNode?.data?.templateNodeId ?? null;
            let leadNodeId = campaignNode?.data?.leadNodeId ?? null;
            let emailNodeName = campaignNode?.data?.emailNodeName ?? null;

            const editor = editorRef.current;
            if (!editor) return { templateNodeId, leadNodeId, emailNodeName };

            const inputConns = campaignNode.inputs?.input_1?.connections || [];
            const outputConns = campaignNode.outputs?.output_1?.connections || [];

            inputConns.forEach((c: any) => {
                const connected = editor.getNodeFromId(c.node);
                if (connected?.data?.type === "template" && !templateNodeId) {
                    const match = templates.find((t) => t.name === connected.name);
                    if (match) templateNodeId = match.id;
                } else if (connected?.data?.type === "lead" && !leadNodeId) {
                    const match = leads.find((l) => l.name === connected.name);
                    if (match) leadNodeId = match.id;
                }
            });

            outputConns.forEach((c: any) => {
                const connected = editor.getNodeFromId(c.node);
                if (connected?.data?.type === "identity" && !emailNodeName) {
                    emailNodeName = connected.data.name;
                }
            });

            return { templateNodeId, leadNodeId, emailNodeName };
        },
        [templates, leads]
    );

    const getEffectiveNodeData = useCallback(
        (node: any): { templateNodeId: number | null; leadNodeId: number | null; emailNodeName: string | null } => {
            if (!node?.data) return { templateNodeId: null, leadNodeId: null, emailNodeName: null };

            let { templateNodeId, leadNodeId, emailNodeName } = getConnectedNodes(node);

            if (!templateNodeId || !leadNodeId || !emailNodeName) {
                const editor = editorRef.current;
                const parent = node.data.parentId ? editor?.getNodeFromId(node.data.parentId) : null;
                if (parent) {
                    const parentData = getEffectiveNodeData(parent);
                    templateNodeId = templateNodeId || parentData.templateNodeId;
                    leadNodeId = leadNodeId || parentData.leadNodeId;
                    emailNodeName = emailNodeName || parentData.emailNodeName;
                }
            }
            return { templateNodeId, leadNodeId, emailNodeName };
        },
        [getConnectedNodes]
    );

    const handleTemplateSave = async () => {
        const templateName = templateNameModal.value.trim();
        const templateTag = templateNameModal.tag.trim();
        if (!templateName) return;

        setTemplateNameModal({ open: false, value: '', tag: '' });
        if (!editorRef.current) return;

        const flowData = editorRef.current.export();
        setIsCanvasLoading(true);
        try {
            const safeName = templateName.toLowerCase().replace(/\s+/g, '-');
            const urls = await getUploadUrls(templateName, templateTag);

            const jsonBlob = new Blob([JSON.stringify(flowData)], { type: "application/json" });
            const htmlBlob = new Blob(["<div>Drawflow Diagram</div>"], { type: "text/html" });

            await Promise.all([
                uploadToS3(urls.jsonUrl, urls.jsonFields, jsonBlob, `${safeName}.json`),
                uploadToS3(urls.htmlUrl, urls.htmlFields, htmlBlob, `${safeName}.html`),
            ]);

            await confirmSave({
                name: templateName,
                tag_name: templateTag,
                description: "Campaign flow diagram",
                template_key: urls.template_key,
                post_status: "204",
                file_secret: urls.file_secret,
                default: false,
            });

            toast.success("Flow template saved successfully!");
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.response?.data || e?.message;
            toast.error(msg || "Failed to save flow template");
        } finally {
            setIsCanvasLoading(false);
        }
    };

    const createCampaignApiCall = useCallback((campaignNode: any, templateNodeId: number, leadNodeId: number, emailNodeName: string | null) => {
        const identityMatch = identities.find((i) => i.email === emailNodeName);
        const friendlyName = identityMatch?.friendlyName || "";

        const body = {
            name: campaignNode.data.name,
            subject: campaignNode.data.subject,
            tid: templateNodeId,
            sdate: campaignNode.data.startDate || new Date().toISOString().split("T")[0],
            edate: campaignNode.data.endDate || new Date().toISOString().split("T")[0],
            cgrp: leadNodeId,
            mailToUse: emailNodeName,
            friendlyName,
            ...(campaignNode.data.parentId ? { parentNodeId: campaignNode.data.parentId } : {}),
        };

        return createCampaignsRes(body)
            .then((response) => ({ response, name: campaignNode.data.name }))
            .catch((error) => ({ error, name: campaignNode.data.name }));
    }, [identities]);

    const saveNodes = async () => {
        const campaignsList = getCampaignNodes();

        if (campaignsList.length === 0) {
            toast.error("No campaign nodes found. Please provide at least one campaign.");
            return;
        }

        const processed = new Set<number>();
        const calls: Array<Promise<any>> = [];

        campaignsList.forEach((node: any) => {
            if (processed.has(node.id)) return;
            processed.add(node.id);

            const { templateNodeId, leadNodeId, emailNodeName } = getEffectiveNodeData(node);

            if (!templateNodeId || !leadNodeId) {
                console.warn(`Skipping node ${node.id}: missing template or lead.`);
                return;
            }
            calls.push(createCampaignApiCall(node, templateNodeId, leadNodeId, emailNodeName));

            if (Array.isArray(node.data.childIds) && node.data.childIds.length > 0) {
                node.data.childIds.forEach((childId: number) => {
                    const child = editorRef.current?.getNodeFromId(childId);
                    if (child && !processed.has(child.id)) {
                        processed.add(child.id);
                        const e = getEffectiveNodeData(child);
                        if (e.templateNodeId && e.leadNodeId) {
                            calls.push(createCampaignApiCall(child, e.templateNodeId, e.leadNodeId, e.emailNodeName));
                        }
                    }
                });
            }
        });

        if (calls.length === 0) return;

        setIsCanvasLoading(true);
        try {
            const results = await Promise.all(calls);
            const errors = results.filter((r) => r.error);
            if (errors.length) {
                errors.forEach(({ error, name }) => {
                    const msg = error?.response?.data?.message || error?.response?.data || error?.message || "Unknown error";
                    toast.error(`Error for campaign "${name}": ${msg}`);
                });
            } else {
                toast.success("Campaign created successfully!");
                navigate("/campaigns");
            }
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.response?.data || e?.message;
            toast.error(msg || "Something went wrong");
        } finally {
            setIsCanvasLoading(false);
        }
    };

    const zoomIn = () => {
        setZoom((z) => z + 0.1);
        editorRef.current?.zoom_in();
    };

    const zoomOut = () => {
        setZoom((z) => z - 0.1);
        editorRef.current?.zoom_out();
    };

    useEffect(() => {
        initDrawflow();
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="flex h-full w-full">

            <Sidebar
                templates={templates}
                leads={leads}
                campaigns={campaigns}
                identities={identities}
                nodesData={nodesData}
                editableNode={editableNode}
                sidebarLoading={sidebarLoading}
                onAddNodeToEditor={addNodeToDrawflow}
                onFetchTemplates={fetchTemplates}
                onFetchLeads={fetchLeads}
                onFetchIdentities={fetchIdentitiesData}
                onFetchCampaigns={fetchCampaignsData}
                onUpdateCampaignNodeData={handleUpdateCampaignNodeData}
                onAddIdentity={(data) => console.log("Add identity:", data)}
                onBackToDashboard={() => navigate("/campaigns")}
                onSaveTemplate={() => setTemplateNameModal({ open: true, value: '', tag: '' })}
            />

            <div id="drawflow-container" className="relative flex-1 h-full dark:bg-dark-900">
                {nodeCount > 0 && (
                    <div className="flow-glass-panel absolute top-4 right-4 z-[10] flex items-center gap-2"
                        style={{ borderRadius: '12px', padding: '6px 8px' }}
                    >
                        <Button onClick={saveNodes} id="save-button" variant="primary"
                            className="h-8 px-4 text-sm font-semibold">Save flow</Button>
                        <button onClick={clearNodes} id="clear-button" className="flow-clear-btn">
                            Clear
                        </button>
                    </div>
                )}

                {nodeCount === 0 && (
                    <div className="absolute inset-0 z-[5] flex flex-col items-center justify-center pointer-events-none select-none">
                        <div className="flex flex-col items-center gap-5 max-w-xs text-center">


                            <div className="canvas-empty-icon-wrap">
                                <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                                    style={{
                                        background: 'linear-gradient(135deg,rgba(108,59,255,0.12),rgba(23,107,255,0.09))',
                                        border: '1.5px dashed rgba(108,59,255,0.35)',
                                    }}
                                >
                                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#6C3BFF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="3" width="7" height="7" rx="1.5" />
                                        <rect x="14" y="3" width="7" height="7" rx="1.5" />
                                        <rect x="3" y="14" width="7" height="7" rx="1.5" />
                                        <rect x="14" y="14" width="7" height="7" rx="1.5" />
                                    </svg>
                                </div>
                            </div>


                            <div className="space-y-1.5">
                                <p className="text-[15px] font-semibold tracking-tight text-gray-700 dark:text-gray-200">
                                    Canvas is empty
                                </p>
                                <p className="text-meta text-content-subtle dark:text-gray-500 leading-relaxed">
                                    Click a step in the left panel to add nodes,<br />
                                    or drag them directly onto the canvas.
                                </p>
                            </div>


                            <div className="flex items-center gap-1.5">
                                <span className="canvas-step-pill">
                                    <span style={{ color: '#6C3BFF', fontSize: '11px' }}>⬡</span> Campaign
                                </span>
                                <span className="text-gray-300 dark:text-gray-600 text-xs">→</span>
                                <span className="canvas-step-pill">
                                    <span style={{ color: '#0EA5E9', fontSize: '11px' }}>⬡</span> Template
                                </span>
                                <span className="text-gray-300 dark:text-gray-600 text-xs">→</span>
                                <span className="canvas-step-pill">
                                    <span style={{ color: '#10B981', fontSize: '11px' }}>⬡</span> Leads
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div
                    id="drawflow"
                    ref={containerRef}
                    className="h-full"
                    style={{ overflow: "hidden" }}
                />

                <div className="flow-glass-panel absolute bottom-4 right-4 z-[10] flex flex-col gap-1.5"
                    style={{ borderRadius: '10px', padding: '4px' }}
                >
                    <Button onClick={zoomIn} variant="ghost" size="icon"
                        className="w-8 h-8 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-600 dark:hover:text-indigo-300 dark:text-gray-300" title="Zoom In">
                        <ZoomIn className="w-4 h-4" />
                    </Button>
                    <div className="h-px bg-gray-200 dark:bg-white/10 mx-1" />
                    <Button onClick={zoomOut} variant="ghost" size="icon"
                        className="w-8 h-8 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-600 dark:hover:text-indigo-300 dark:text-gray-300" title="Zoom Out">
                        <ZoomOut className="w-4 h-4" />
                    </Button>
                </div>

                {isCanvasLoading && (
                    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-50/70 dark:bg-[#0b0f19]/85 backdrop-blur-md transition-all duration-300">
                        <div className="flow-glass-panel rounded-3xl p-8 max-w-xs w-full flex flex-col items-center border border-white/10 dark:border-white/5 shadow-2xl"
                            style={{ borderRadius: '24px' }}>
                            <div className="relative flex items-center justify-center w-20 h-20 mb-6">
                                <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-indigo-500 border-r-purple-500 animate-spin" style={{ animationDuration: '1.2s' }} />
                                <div className="absolute inset-2 rounded-full border border-transparent border-b-sky-400 border-l-indigo-400 animate-spin" style={{ animationDuration: '0.8s', animationDirection: 'reverse' }} />
                                <div className="absolute inset-4 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30 animate-pulse">
                                    <Zap className="w-5 h-5 text-white" />
                                </div>
                            </div>
                            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 tracking-tight mb-1">
                                Campaign Builder
                            </h3>
                            <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">
                                Initializing workspace...
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {templateNameModal.open && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="bg-surface dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 flex flex-col gap-4"
                        style={{ border: '1px solid rgba(108,59,255,0.18)' }}
                    >
                        <div>
                            <h3 className="text-base font-semibold text-content dark:text-white mb-0.5">Save as Template</h3>
                            <p className="text-xs text-content-subtle dark:text-gray-500">Give your flow template a name before saving.</p>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold text-content-secondary dark:text-gray-400">TEMPLATE NAME</label>
                            <input
                                autoFocus
                                type="text"
                                placeholder="e.g. Welcome Campaign Flow"
                                value={templateNameModal.value}
                                onChange={(e) => setTemplateNameModal((s) => ({ ...s, value: e.target.value }))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleTemplateSave();
                                    if (e.key === 'Escape') setTemplateNameModal({ open: false, value: '', tag: '' });
                                }}
                                className="w-full px-3.5 py-2.5 rounded-xl border border-line dark:border-dark-600
                                    bg-gray-50 dark:bg-dark-900 text-sm text-content dark:text-white
                                    placeholder-gray-400 dark:placeholder-dark-500
                                    focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400 transition-all"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold text-content-secondary dark:text-gray-400">TAG (Optional)</label>
                            <input
                                type="text"
                                placeholder="e.g. Medical"
                                value={templateNameModal.tag}
                                onChange={(e) => setTemplateNameModal((s) => ({ ...s, tag: e.target.value }))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleTemplateSave();
                                    if (e.key === 'Escape') setTemplateNameModal({ open: false, value: '', tag: '' });
                                }}
                                className="w-full px-3.5 py-2.5 rounded-xl border border-line dark:border-dark-600
                                    bg-gray-50 dark:bg-dark-900 text-sm text-content dark:text-white
                                    placeholder-gray-400 dark:placeholder-dark-500
                                    focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400 transition-all"
                            />
                        </div>
                        <div className="flex gap-2.5 justify-end mt-2">
                            <button
                                onClick={() => setTemplateNameModal({ open: false, value: '', tag: '' })}
                                className="px-4 py-2 rounded-xl border border-line dark:border-dark-600
                                    bg-surface dark:bg-dark-900 text-sm font-semibold text-content-secondary dark:text-gray-400
                                    hover:bg-gray-50 dark:hover:bg-dark-800 cursor-pointer transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleTemplateSave}
                                disabled={!templateNameModal.value.trim()}
                                className="px-5 py-2 rounded-xl border-0 text-sm font-bold cursor-pointer transition-all
                                    bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-sm
                                    hover:opacity-90 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DrawflowComponent;
